import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import api from '../api/client';
import { SavedServer, StewardingPenalty } from '../types';
import './Stewarding.css';

interface StewardingProps {
  server: SavedServer;
}

interface ResultFile {
  filename: string;
  date: string;
  time: string;
  type: string;
  size: number;
}

interface ResultEntry {
  driverName: string;
  driverId?: string;
  raceNumber: string;
}

const Stewarding: React.FC<StewardingProps> = ({ server }) => {
  const [results, setResults] = useState<ResultFile[]>([]);
  const [resultEntries, setResultEntries] = useState<ResultEntry[]>([]);
  const [penalties, setPenalties] = useState<StewardingPenalty[]>(server.penalties || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [driverName, setDriverName] = useState('');
  const [driverId, setDriverId] = useState('');
  const [raceNumber, setRaceNumber] = useState('');
  const [seconds, setSeconds] = useState(5);
  const [reason, setReason] = useState('');

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getResults(server.id, 'race');
      const raceResults = res.data.results || [];
      setResults(raceResults);
      if (raceResults.length > 0 && !selectedFile) {
        setSelectedFile(raceResults[0].filename);
      }
    } catch (err) {
      console.error('Failed to load race results:', err);
      setError('Failed to load race results');
    } finally {
      setLoading(false);
    }
  }, [server.id, selectedFile]);

  const loadPenalties = useCallback(async () => {
    try {
      const res = await api.getPenalties(server.id);
      setPenalties(res.data.penalties || []);
    } catch (err) {
      console.error('Failed to load penalties:', err);
      setError('Failed to load penalties');
    }
  }, [server.id]);

  const loadResultEntries = useCallback(async (filename: string) => {
    if (!filename) {
      setResultEntries([]);
      return;
    }
    try {
      const res = await api.viewResult(server.id, filename);
      const entries = (res.data.standings || [])
        .filter((standing: any) => standing.driverName && standing.raceNumber !== undefined)
        .map((standing: any) => ({
          driverName: standing.driverName,
          driverId: standing.driverId,
          raceNumber: String(standing.raceNumber),
        }))
        .filter((entry: ResultEntry, index: number, self: ResultEntry[]) =>
          index === self.findIndex((e) => e.driverName === entry.driverName && e.raceNumber === entry.raceNumber)
        );
      setResultEntries(entries);
    } catch (err) {
      console.error('Failed to load result driver entries:', err);
      setResultEntries([]);
    }
  }, [server.id]);

  useEffect(() => {
    loadResults();
    loadPenalties();
  }, [loadResults, loadPenalties]);

  useEffect(() => {
    if (selectedFile) {
      loadResultEntries(selectedFile);
    } else {
      setResultEntries([]);
    }
  }, [selectedFile, loadResultEntries]);

  const clearForm = () => {
    setDriverName('');
    setDriverId('');
    setRaceNumber('');
    setSeconds(5);
    setReason('');
    setError(null);
    setSuccess(null);
  };

  const selectEntry = (entry?: ResultEntry) => {
    if (!entry) return;
    setDriverName(entry.driverName);
    setDriverId(entry.driverId || '');
    setRaceNumber(entry.raceNumber);
  };

  const handleDriverNameChange = (value: string) => {
    setDriverName(value);
    const matched = resultEntries.find((entry) => entry.driverName.toLowerCase() === value.trim().toLowerCase());
    if (matched) {
      selectEntry(matched);
    }
  };

  const handleRaceNumberChange = (value: string) => {
    setRaceNumber(value);
    const matched = resultEntries.find((entry) => entry.raceNumber === value);
    if (matched) {
      selectEntry(matched);
    }
  };

  const handleDriverIdChange = (value: string) => {
    setDriverId(value);
    const matched = resultEntries.find((entry) => entry.driverId === value);
    if (matched) {
      selectEntry(matched);
    }
  };

  const handleAddPenalty = async () => {
    if (!selectedFile) {
      setError('Choose a result file first');
      return;
    }
    if (!raceNumber.trim()) {
      setError('Race number is required');
      return;
    }
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    if (seconds <= 0) {
      setError('Penalty seconds must be greater than zero');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const selectedEntry = resultEntries.find((entry) => entry.raceNumber === raceNumber.trim());
      const payload = {
        resultFilename: selectedFile,
        driverId: driverId.trim() || selectedEntry?.driverId || undefined,
        driverName: driverName.trim() || selectedEntry?.driverName || undefined,
        raceNumber: raceNumber.trim(),
        seconds,
        reason: reason.trim(),
      };

      const res = await api.addPenalty(server.id, payload);
      setPenalties((current) => [...current, res.data.penalty]);
      setSuccess('Penalty added successfully');
      clearForm();
    } catch (err) {
      console.error('Failed to add penalty:', err);
      setError('Failed to add penalty');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePenalty = async (penaltyId: string) => {
    try {
      setSaving(true);
      setError(null);
      await api.deletePenalty(server.id, penaltyId);
      setPenalties((current) => current.filter((p) => p.id !== penaltyId));
      setSuccess('Penalty removed');
    } catch (err) {
      console.error('Failed to remove penalty:', err);
      setError('Failed to remove penalty');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stewarding-container">
      <div className="stewarding-header">
        <div>
          <h3>Stewarding</h3>
          <p>Apply post-race penalties separately from the result JSON.</p>
        </div>
      </div>

      <div className="stewarding-grid">
        <div className="stewarding-panel">
          <h4>Add Penalty</h4>
          <div className="form-group">
            <label>Result file</label>
            <select
              value={selectedFile}
              onChange={(e) => {
                setSelectedFile(e.target.value);
                clearForm();
              }}
            >
              <option value="">Select a race result</option>
              {results.map((result) => (
                <option key={result.filename} value={result.filename}>
                  {result.filename}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Driver name</label>
            <input
              list="driverNames"
              value={driverName}
              onChange={(e) => handleDriverNameChange(e.target.value)}
              placeholder="Start typing a driver name"
            />
            <datalist id="driverNames">
              {resultEntries.map((entry) => (
                <option key={`${entry.raceNumber}-${entry.driverName}`} value={entry.driverName} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label>Driver ID</label>
            <input
              list="driverIds"
              value={driverId}
              onChange={(e) => handleDriverIdChange(e.target.value)}
              placeholder="Optional driver ID"
            />
            <datalist id="driverIds">
              {resultEntries
                .filter((entry) => entry.driverId)
                .map((entry) => (
                  <option key={`${entry.raceNumber}-${entry.driverId}`} value={entry.driverId || ''} />
                ))}
            </datalist>
          </div>
          <div className="form-group">
            <label>Race number</label>
            <select value={raceNumber} onChange={(e) => handleRaceNumberChange(e.target.value)}>
              <option value="">Select a race number</option>
              {resultEntries.map((entry) => (
                <option key={`${entry.raceNumber}-${entry.driverName}`} value={entry.raceNumber}>
                  {entry.raceNumber} — {entry.driverName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Penalty seconds</label>
            <input
              type="number"
              value={seconds}
              min={1}
              onChange={(e) => setSeconds(Number(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label>Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain the offense" />
          </div>
          <div className="form-actions">
            <button className="add-button" onClick={handleAddPenalty} disabled={saving}>
              <PlusCircle size={16} />
              {saving ? 'Saving...' : 'Add Penalty'}
            </button>
          </div>
          {error && <div className="stewarding-error">{error}</div>}
          {success && <div className="stewarding-success">{success}</div>}
        </div>

        <div className="stewarding-panel">
          <h4>Active Penalties</h4>
          <div className="penalties-list">
            {penalties.length === 0 ? (
              <div className="empty-state">No penalties registered yet.</div>
            ) : (
              penalties.map((penalty) => (
                <div key={penalty.id} className="penalty-card">
                  <div className="penalty-meta">
                    <div className="penalty-row">
                      <span className="penalty-label">File</span>
                      <span>{penalty.resultFilename}</span>
                    </div>
                    <div className="penalty-row">
                      <span className="penalty-label">Driver</span>
                      <span>{penalty.driverName}</span>
                    </div>
                    {penalty.raceNumber && (
                      <div className="penalty-row">
                        <span className="penalty-label">Number</span>
                        <span>{penalty.raceNumber}</span>
                      </div>
                    )}
                    {penalty.driverId && (
                      <div className="penalty-row">
                        <span className="penalty-label">ID</span>
                        <span>{penalty.driverId}</span>
                      </div>
                    )}
                    <div className="penalty-row">
                      <span className="penalty-label">Seconds</span>
                      <span>+{penalty.seconds}s</span>
                    </div>
                  </div>
                  <div className="penalty-footer">
                    <div className="penalty-reason">{penalty.reason}</div>
                    <button
                      className="remove-button"
                      onClick={() => handleDeletePenalty(penalty.id)}
                      disabled={saving}
                    >
                      <Trash2 size={14} />
                      Undo
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stewarding;
